import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Loader2, Clock, CalendarClock, FileText, Banknote, ChevronRight } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { API, formatApiErrorDetail } from "../context/AuthContext";
import { useFmtXof } from "../lib/i18nFmt";

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const hoursBetween = (a, b) => {
  if (!a || !b) return 0;
  const [h1, m1] = a.split(":").map(Number);
  const [h2, m2] = b.split(":").map(Number);
  const delta = (h2 * 60 + m2 - (h1 * 60 + m1)) / 60;
  return Math.round(delta * 100) / 100;
};

export default function SessionPointageDialog({ open, onOpenChange, bookings = [], onCreated }) {
  const { t } = useTranslation();
  const fmt = useFmtXof();
  const [bookingId, setBookingId] = useState("");
  const [date, setDate] = useState(todayIso());
  const [startTime, setStartTime] = useState("15:00");
  const [endTime, setEndTime] = useState("17:00");
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && bookings.length > 0 && !bookingId) setBookingId(bookings[0].id);
    if (!open) setContent("");
  }, [open, bookings, bookingId]);

  const selected = bookings.find((b) => b.id === bookingId);
  const rate = Number(selected?.rate_xof || (selected?.amount_xof && selected?.hours ? selected.amount_xof / selected.hours : 0));
  const duration = hoursBetween(startTime, endTime);
  const gross = Math.round(rate * duration);
  const fee = Math.round(gross * 0.15);
  const net = gross - fee;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!bookingId) return toast.error(t("pointage.errBooking"));
    if (duration <= 0) return toast.error(t("pointage.errTime"));
    if (content.trim().length < 10) return toast.error(t("pointage.errContent"));
    setSubmitting(true);
    try {
      await axios.post(`${API}/sessions`, { booking_id: bookingId, date, start_time: startTime, end_time: endTime, content: content.trim() });
      toast.success(t("pointage.submitted"));
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg" data-testid="session-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#0A2518]">{t("pointage.title")}</DialogTitle>
          <DialogDescription>{t("pointage.subtitle")}</DialogDescription>
        </DialogHeader>

        {bookings.length === 0 ? (
          <div className="rounded-xl bg-yellow-50 border border-yellow-200 p-4 text-sm text-yellow-800" data-testid="session-no-bookings">
            {t("pointage.noBookings")}
          </div>
        ) : (
          <form onSubmit={onSubmit} className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="text-[#0A2518]">{t("pointage.bookingLabel")}</Label>
              <select value={bookingId} onChange={(e) => setBookingId(e.target.value)} className="mt-1.5 w-full h-11 rounded-md border border-input bg-white px-3 text-sm" data-testid="session-booking-select">
                {bookings.map((b) => (
                  <option key={b.id} value={b.id}>
                    {t("pointage.bookingOption", { parent: b.parent_name || "Parent", subject: b.subject, hours: b.hours, amount: fmt(b.amount_xof) })}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-[#0A2518] flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> {t("pointage.dateLabel")}</Label>
              <Input type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} className="mt-1.5 h-11" data-testid="session-date-input" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[#0A2518] text-xs">{t("pointage.startLabel")}</Label>
                <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="mt-1.5 h-11" data-testid="session-start-input" />
              </div>
              <div>
                <Label className="text-[#0A2518] text-xs">{t("pointage.endLabel")}</Label>
                <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="mt-1.5 h-11" data-testid="session-end-input" />
              </div>
            </div>
            <div className="col-span-2">
              <Label className="text-[#0A2518] flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> {t("pointage.contentLabel")}</Label>
              <Textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder={t("pointage.contentPh")} className="mt-1.5 min-h-[110px]" data-testid="session-content-input" />
              <p className="mt-1 text-xs text-[#4B5563]">{t("pointage.contentHint")}</p>
            </div>

            <div className="col-span-2 rounded-xl bg-[#F0FDF4] border border-[#16A34A]/20 p-3 text-sm">
              <div className="flex items-center justify-between"><span className="text-[#4B5563] flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-[#16A34A]" /> {t("pointage.duration")}</span><span className="font-medium" data-testid="session-duration">{duration} h</span></div>
              <div className="flex items-center justify-between"><span className="text-[#4B5563] flex items-center gap-1.5"><Banknote className="h-3.5 w-3.5 text-[#16A34A]" /> {t("pointage.gross")}</span><span>{fmt(gross)}</span></div>
              <div className="flex items-center justify-between text-xs"><span className="text-[#4B5563]">{t("pointage.commission")}</span><span>{fmt(fee)}</span></div>
              <div className="flex items-center justify-between mt-1 pt-1 border-t border-[#16A34A]/20 font-display"><span className="text-[#0A2518] font-semibold">{t("pointage.netToPay")}</span><span className="text-[#15803D] font-bold" data-testid="session-net">{fmt(net)}</span></div>
            </div>

            <DialogFooter className="col-span-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>
              <Button type="submit" disabled={submitting} className="rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white" data-testid="session-submit-btn">
                {submitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("pointage.submitting")}</>) : (<>{t("pointage.submit")} <ChevronRight className="h-4 w-4 ml-1" /></>)}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
